/**
 * Примитивы интерфейса исполнительных схем — общие для раздела ОСНОВА.Геодезист
 * и вкладки «Исполнительные схемы» в ОСНОВА.Инженер.
 *
 * Тон — «камеральный стол»: тонкие линейки, моноширинные цифры координат,
 * миллиметровка под схемами листа. Всё — на токенах темы.
 */
import type { ReactNode } from 'react';
import styled, { css, type DefaultTheme } from 'styled-components';
import type { GeoTone } from '@entities/geodesy';

export const Panel = styled.section`
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(5)};
  min-width: 0;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
  text-wrap: balance;
`;

export const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 72ch;
  text-wrap: pretty;
`;

export const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Stack = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $gap = 4 }) => theme.spacing($gap)};
  min-width: 0;
`;

export const Row = styled.div<{ $gap?: number; $align?: 'center' | 'flex-end' | 'flex-start' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: ${({ $align = 'center' }) => $align};
  gap: ${({ theme, $gap = 3 }) => theme.spacing($gap)};
`;

export const FieldGrid = styled.div<{ $min?: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${({ $min = 180 }) => `${$min}px`}, 1fr));
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
`;

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};

  /* Поля внутри подписи наследуют шрифт (font: inherit) — значения не жирные. */
  input,
  select,
  textarea {
    font-weight: 400;
  }
`;

export const FieldNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  line-height: 1.5;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Значение-число: моноширинное, табличные цифры. */
export const Num = styled.span<{ $tone?: GeoTone; $strong?: boolean }>`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: 0.95em;
  white-space: nowrap;
  font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
`;

function toneColor(theme: DefaultTheme, tone: GeoTone | undefined): string {
  switch (tone) {
    case 'success':
    case 'warning':
    case 'danger':
    case 'info':
      return theme.colors.status[tone];
    case 'neutral':
      return theme.colors.text.secondary;
    default:
      return 'inherit';
  }
}

/** Плашка-сообщение с цветной кромкой. `role` задаёт вызывающий (alert/status). */
export const Notice = styled.div<{ $tone: Exclude<GeoTone, 'neutral'> | 'neutral' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13.5px;
  line-height: 1.5;
  text-wrap: pretty;
  ${({ theme, $tone }) =>
    $tone === 'neutral'
      ? css`
          background: ${theme.colors.surface};
          border: 1px solid ${theme.colors.border};
          border-left: 3px solid ${theme.colors.borderStrong};
          color: ${theme.colors.text.primary};
        `
      : css`
          background: ${theme.colors.status[$tone]}12;
          border: 1px solid ${theme.colors.status[$tone]}33;
          border-left: 3px solid ${theme.colors.status[$tone]};
          color: ${theme.colors.text.primary};
        `}

  & > strong {
    font-weight: 600;
  }
`;

export const ErrorText = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.status.danger};
`;

/** Прокручиваемая обёртка таблицы (горизонталь — на узких экранах). */
export const TableWrap = styled.div<{ $maxHeight?: number }>`
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : 'none')};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

/** Плотная таблица данных: числа — справа и моноширинно (`td.num`). */
export const DataTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  td {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: middle;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  th.num,
  td.num {
    text-align: right;
  }

  td.num {
    font-family: ${({ theme }) => theme.font.mono};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  tbody tr:hover td {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

/** Строка таблицы с подсветкой проблемы (превышение / требует внимания). */
export const ToneRow = styled.tr<{ $tone?: 'danger' | 'warning' | 'muted' }>`
  ${({ theme, $tone }) =>
    $tone === 'danger'
      ? css`
          td {
            background: ${theme.colors.status.danger}0D;
          }
          td:first-child {
            box-shadow: inset 3px 0 0 ${theme.colors.status.danger};
          }
        `
      : $tone === 'warning'
        ? css`
            td:first-child {
              box-shadow: inset 3px 0 0 ${theme.colors.status.warning};
            }
          `
        : $tone === 'muted'
          ? css`
              td {
                color: ${theme.colors.text.secondary};
              }
            `
          : ''}
`;

/** Кнопка-«чип» выбора (aria-pressed задаёт вызывающий). */
export const Chip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  min-height: 30px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const ChoiceLabel = styled.label<{ $checked: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $checked }) => ($checked ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $checked }) =>
    $checked ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  transition: border-color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme, $disabled }) => ($disabled ? theme.colors.border : theme.colors.primary)};
  }

  &:has(input:focus-visible) {
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
  }
`;

const ChoiceTitle = styled.span<{ $checked: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme, $checked }) => ($checked ? theme.colors.primary : theme.colors.text.primary)};
`;

const ChoiceSub = styled.span`
  font-size: 12.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Вариант выбора-«карточка» на нативном radio (клавиатура — штатная). */
export function ChoiceCard({
  name,
  checked,
  disabled = false,
  onSelect,
  title,
  children,
}: {
  name: string;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <ChoiceLabel $checked={checked} $disabled={disabled}>
      <input type="radio" name={name} checked={checked} disabled={disabled} onChange={onSelect} />
      <ChoiceTitle $checked={checked}>{title}</ChoiceTitle>
      {children && <ChoiceSub>{children}</ChoiceSub>}
    </ChoiceLabel>
  );
}

export const ChoiceGrid = styled.div<{ $min?: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${({ $min = 150 }) => `${$min}px`}, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
`;

const CheckWrap = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  input {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin: 2px 0 0;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: inherit;
  }
`;

/** Чекбокс с подписью. */
export function Check({
  checked,
  onChange,
  disabled = false,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <CheckWrap $disabled={disabled}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{children}</span>
    </CheckWrap>
  );
}

/** Плитка сводки: крупное число + подпись. */
export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
`;

const KpiBox = styled.div<{ $tone?: GeoTone }>`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  ${({ theme, $tone }) =>
    $tone && $tone !== 'neutral'
      ? css`
          border-color: ${theme.colors.status[$tone]}40;
          background: ${theme.colors.status[$tone]}0F;
        `
      : ''}
`;

const KpiValue = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const KpiLabel = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export function Kpi({ value, label, tone }: { value: ReactNode; label: string; tone?: GeoTone }) {
  return (
    <KpiBox $tone={tone}>
      <KpiValue>{value}</KpiValue>
      <KpiLabel>{label}</KpiLabel>
    </KpiBox>
  );
}

/** Список «ключ — значение» (сведения о листе, штамп РД). */
export const Facts = styled.dl`
  display: grid;
  grid-template-columns: minmax(120px, max-content) 1fr;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(4)}`};
  margin: 0;
  font-size: 13.5px;

  dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

/** Ссылка-кнопка без фона (действие внутри текста/таблицы). */
export const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.primaryHover};
    text-decoration: underline;
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.text.muted};
    cursor: not-allowed;
  }
`;

/** Маленькая квадратная кнопка-иконка (удалить строку, править). */
export const IconButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.border};
    color: ${({ theme, $danger }) =>
      $danger ? theme.colors.status.danger : theme.colors.text.primary};
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/** Кнопки действий в ячейке таблицы — в одну строку. */
export const CellActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
`;

/** Компактное поле ввода в ячейке таблицы. */
export const CellInput = styled.input`
  width: 100%;
  min-width: 72px;
  height: 30px;
  padding: ${({ theme }) => `0 ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;

  &.num {
    font-family: ${({ theme }) => theme.font.mono};
    font-variant-numeric: tabular-nums;
    text-align: right;
  }
  &[aria-invalid='true'] {
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const CellSelect = styled.select`
  height: 30px;
  padding: ${({ theme }) => `0 ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/** Только для скринридера. */
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

/** Отключает все поля формы разом (режим только чтения). */
export const Fieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: none;
  min-width: 0;
`;
